<template>
  <div class="opportunity-detail-page">
    <OpportunityHeader />
    <BaseLoadingBar v-if="opportunityPending" />

    <div class="opportunity-detail-page__body" v-else-if="opportunity">
      <div class="opportunity-detail-page__left">
        <OpportunityMetaCard :opportunity-id="opportunityId" />
        <div class="opportunity-detail-page__base-card" v-if="!tenantIsRestricted">
          <MarkdownEditor
            :title="$t('page.opportunity-details.notes.title')"
            :value="(opportunity && opportunity.notes) || ''"
            :saving="notesSaving"
            :editor-options="editorOptions"
            :auto-save="true"
            :on-conflict-refresh="refreshOpportunityBeforeEditing"
            :before-edit="refreshOpportunityBeforeEditing"
            @input="(v) => $set(opportunity, 'notes', v)"
            @save="(markdown) => saveOpportunityNotes(markdown)"
          />
        </div>
        <div class="opportunity-detail-page__base-card" v-if="tenantHasDocumentsAccess()">
          <div
            class="opportunity-detail-page__card-title-container opportunity-detail-page__card-title-container--icon-actions"
          >
            <div class="opportunity-detail-page__card-title">
              <span>{{ $t('tender-details.documents') }} ({{ opportunity.documents.length }})</span>
            </div>
            <div class="opportunity-detail-page__card-title-right-section">
              <template v-if="tenantHasChromeExtensionAccess() && hasLinkedTender">
                <DownloadDocumentsButton />
                <DesktopDownloadDocumentsButton :opportunity="opportunity" @documents-uploaded="onDocumentsUploaded" />
              </template>
              <DownloadAllDocumentsButton v-if="opportunity.documents.length > 0" :opportunity="opportunity" />
            </div>
          </div>
          <v-popover
            v-if="missingTenderDocuments.length > 0"
            placement="top"
            trigger="hover"
            popoverClass="basic-tooltip left-aligned"
            :delay="{ show: 500, hide: 200 }"
          >
            <BaseInfoBanner
              class="opportunity-detail-page__missing-docs-banner"
              tone="warning"
              preset="soft"
              :title="
                $tc('page.opportunity-details.missing-tender-documents', missingTenderDocuments.length, {
                  count: missingTenderDocuments.length,
                })
              "
            >
              <template #icon>
                <BaseIcon name="alert-triangle" />
              </template>
            </BaseInfoBanner>
            <template v-slot:popover>
              <div v-for="(document, index) in missingTenderDocuments" :key="index">
                {{ document }}
              </div>
            </template>
          </v-popover>
          <OpportunityDocuments
            :opportunity="opportunity"
            @documents-uploaded="onDocumentsUploaded"
            @document-deleted="onDocumentDeleted"
          />
          <div v-if="tender" id="tender-id" class="extension-hook__information">{{ tender.id }}</div>
          <div v-if="opportunity" id="opportunity-id" class="extension-hook__information">{{ opportunity.id }}</div>
        </div>
        <div v-if="tenantLocations.length > 0" class="opportunity-detail-page__base-card">
          <div class="opportunity-detail-page__card-subtitle">
            {{ $t('page.opportunity-details.mobilization.title') }}
          </div>
          <div>
            <div class="opportunity-detail-page__card-subtitle">
              <span class="opportunity-detail-page__card-subtitle--text">{{
                $t('page.opportunities.data-table-titles.job-location')
              }}</span>
              <BaseButton
                isSmall
                isUnderline
                isTransparent
                isSquare
                @click.native="openJobLocationDialog"
                :has-icon-left="!jobLocationText"
              >
                <BaseIcon v-if="!jobLocationText" name="plus" class="opportunity-detail-page__add-icon" />
                {{ jobLocationText ? $t('actions.edit') : $t('actions.add') }}
              </BaseButton>
            </div>
            <span class="opportunity-detail-page__card-empty-text">{{
              jobLocationText ? jobLocationText : $t('page.opportunity-details.mobilization.empty')
            }}</span>
            <BaseInfoBanner
              v-if="jobLocationNoticeKey"
              class="opportunity-detail-page__location-notice"
              tone="warning"
              preset="soft"
              :title="$t(jobLocationNoticeKey)"
            >
              <template #icon><BaseIcon name="alert-triangle" /></template>
            </BaseInfoBanner>
          </div>

          <OpportunityMobilizations
            class="opportunity-detail-page__mobilization"
            :opportunity="opportunity"
            :show-location-if-no-route="
              jobLocationNoticeKey === 'page.opportunity-details.mobilization.approximate-province'
            "
          />
        </div>
        <div class="opportunity-detail-page__base-card">
          <div class="opportunity-detail-page__card-content">
            <div v-if="!tender">
              <div class="opportunity-detail-page__card-subtitle">
                <span class="opportunity-detail-page__card-subtitle--text">{{
                  $t('page.opportunities.data-table-titles.source')
                }}</span>
              </div>
              <div>
                <span>{{ getOpportunitySourceName(opportunity.source) }}</span>
                <span v-if="hasLinkedTender"> · {{ $t('page.opportunities.new.tender-unavailable') }}</span>
              </div>
            </div>
            <div v-if="!tender">
              <div class="opportunity-detail-page__card-subtitle">
                <span class="opportunity-detail-page__card-subtitle--text">{{
                  $t('page.opportunities.data-table-titles.emitter')
                }}</span>
              </div>
              <div>
                <span>{{ opportunity.emitter }}</span>
              </div>
            </div>
            <div>
              <div class="opportunity-detail-page__card-subtitle">
                <span class="opportunity-detail-page__card-subtitle--text">{{
                  $t('page.opportunities.data-table-titles.dates')
                }}</span>
                <!-- On phones, the calendar in a sheet, as the other date pickers (docs/page-layout.md). -->
                <v-menu
                  ref="datePickerMenu"
                  :disabled="isPhone"
                  :close-on-content-click="false"
                  :return-value.sync="dates"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on" v-bind="attrs" @click="openDateSheet">
                      <BaseButton isSmall isUnderline isTransparent isSquare @click.native="onEditDate">
                        {{ $t('actions.edit') }}
                      </BaseButton>
                    </div>
                  </template>
                  <DatePicker ref="datePicker" :propDates="dates" range range-required @on-save-dates="saveDates" />
                </v-menu>
                <BaseBottomSheet
                  v-if="isPhone"
                  v-model="dateSheetOpen"
                  :title="$t('page.opportunities.data-table-titles.dates')"
                >
                  <DatePicker
                    v-if="dateSheetOpen"
                    :propDates="dates"
                    range
                    range-required
                    @on-close-menu="dateSheetOpen = false"
                    @on-save-dates="saveDates"
                  />
                </BaseBottomSheet>
              </div>
              <div class="opportunity-detail-page__dates">
                <div class="opportunity-detail-page__chip">
                  {{ formatStandardDate(dates[0]) }}
                </div>
                {{ this.$t('basic-words.to') }}
                <div class="opportunity-detail-page__chip">
                  {{ formatStandardDate(dates[1]) }}
                </div>
              </div>
              <!-- <span class="opportunity-detail-page__chip">{{ datesRangeText }}</span> -->
            </div>

            <div>
              <div class="opportunity-detail-page__card-subtitle">
                <span class="opportunity-detail-page__card-subtitle--text">{{
                  $t('page.opportunities.data-table-titles.potential-bidders')
                }}</span>
                <BaseButton isSmall isUnderline isTransparent isSquare @click.native="onAddPotentialBidder">
                  <BaseIcon name="plus" class="opportunity-detail-page__add-icon" />
                  {{ $t('actions.add') }}
                </BaseButton>
              </div>
              <BaseCircularLoadingBar :size="14" v-if="potentielBiddersPending" :width="2" color="accent" />
              <div
                class="opportunity-detail-page__card-empty-text"
                v-else-if="!potentielBiddersPending && potentialBidders.length <= 0"
              >
                {{ $t('page.tenders.none') }}
              </div>
              <div class="opportunity-detail-page__potential-bidders-tags" v-else>
                <template v-for="potentialBidder in manualPotentialBidders">
                  <BasePotentialBidder
                    :potentialBidder="potentialBidder"
                    :actionable="true"
                    :key="potentialBidder.id"
                    @edit-potential-bidder="onEditPotentialBidder(potentialBidder)"
                    @delete-potential-bidder="onDeletePotentialBidder(potentialBidder)"
                  />
                </template>
              </div>
            </div>
            <div>
              <div class="opportunity-detail-page__card-subtitle">
                <span class="opportunity-detail-page__card-subtitle--text">{{
                  $t('page.opportunities.data-table-titles.author')
                }}</span>
              </div>
              <div class="opportunity-detail-page__created-at">
                <div class="opportunity-detail-page__creator">
                  <BaseAvatar
                    :name="getFullName(opportunity.authorId)"
                    :color="isCurrentUser(opportunity.authorId) ? '#fcdbd9' : '#d9e5f9'"
                  />
                  <div class="opportunity-detail-page__creator-identity">
                    <div>{{ getFullName(opportunity.authorId) }}</div>
                    <div class="opportunity-detail-page__creator-email">{{ emailFinder(opportunity.authorId) }}</div>
                  </div>
                </div>
                <div class="opportunity-detail-page__create-date">
                  <div>{{ $t('page.opportunity-details.creation-date') }}</div>
                  {{ formatStandardDate(opportunity.createdAt) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="opportunity-detail-page__center">
        <div class="opportunity-detail-page__base-card" v-if="!shouldShowDocumentsModule">
          <MarkdownEditor
            :title="$t('page.opportunity-details.notes.title')"
            :value="(opportunity && opportunity.notes) || ''"
            :saving="notesSaving"
            :editor-options="editorOptions"
            :auto-save="true"
            :on-conflict-refresh="refreshOpportunityBeforeEditing"
            :before-edit="refreshOpportunityBeforeEditing"
            @input="(v) => $set(opportunity, 'notes', v)"
            @save="(markdown) => saveOpportunityNotes(markdown)"
          />
        </div>
        <div class="opportunity-detail-page__card" v-if="shouldShowDocumentsModule">
          <div class="opportunity-detail-page__card-title-container">
            <div class="opportunity-detail-page__grid-heading">
              <div class="opportunity-detail-page__card-title">
                {{ $t('menu.admin-evaluation-grid') }}
                <BaseIcon v-if="!tenantHasDocumentsAccess()" name="lock" class="opportunity-detail-page__lock-icon" />
              </div>
              <BaseMenu
                v-if="!opportunityPending && tenantHasDocumentsAccess()"
                :title="$t('menu.admin-evaluation-grid')"
                :items="evaluationGridTemplates"
                :selection="selectedTemplate"
                is-unclearable
                :is-multiple="false"
                :is-disabled="isEvalGridFieldsExtracting"
                @select-item="onTemplateSelect"
                ref="baseStatusMenu"
                :attach="'.opportunity-detail-page__switch-evaluation-grid'"
              >
                <template v-slot:activator>
                  <BaseButton
                    isSmall
                    isOutlined
                    isSquare
                    hasIconRight
                    :disabled="isEvalGridFieldsExtracting"
                    v-tooltip="{
                      content: isEvalGridFieldsExtracting
                        ? $t('actions.extraction-in-progress')
                        : $t('evaluation-grid.change-template'),
                      placement: 'top',
                      offset: '0, 12',
                    }"
                    class="opportunity-detail-page__switch-evaluation-grid"
                  >
                    <span>{{ evaluationGridTemplateName || $t('evaluation-grid.select-template') }}</span>
                    <BaseIcon name="chevron-down" />
                  </BaseButton>
                </template>
                <template v-slot:default="{ item }">
                  <div class="items">
                    {{ item.name }}
                  </div>
                </template>
              </BaseMenu>
            </div>
            <div>
              <div
                class="opportunity-detail-page__card-title-right-section"
                v-if="
                  (!evaluationGridLoading && isEvaluationGridLoaded && isEvaluationGridEmpty) ||
                  !tenantHasLLMExtractionsAccess()
                "
              >
                <BaseButton
                  :size="44"
                  has-icon-left
                  is-small
                  is-square
                  icon="rotate-cw"
                  is-ai
                  :tooltipText="
                    !tenantHasLLMExtractionsAccess()
                      ? $t('shared.upgrade')
                      : !hasDocuments
                      ? $t('page.opportunity-details.no-pdf-document-message')
                      : isEvalGridFieldsExtracting
                      ? $t('actions.extraction-in-progress')
                      : $t('page.opportunity-details.generate-tooltip')
                  "
                  tooltipPlacement="top"
                  @click.native="onLlmExtractionGenerateClick(null)"
                  :disabled="!tenantHasLLMExtractionsAccess() || !hasDocuments || isEvalGridFieldsExtracting"
                >
                  <BaseIcon name="ai" class="ai-icon" />
                  {{ $t('page.opportunity-details.generate') }}
                </BaseButton>
              </div>
              <div
                class="opportunity-detail-page__card-title-right-section"
                v-if="!isEvaluationGridEmpty && opportunity.documents.length > 0"
              >
                <BaseRoundButton
                  v-if="tenantHasLLMExtractionsAccess()"
                  :size="44"
                  is-transparent
                  icon="rotate-cw"
                  :tooltip-text="
                    isEvalGridFieldsExtracting
                      ? $t('actions.extraction-in-progress')
                      : $t('page.opportunity-details.regenerate-tooltip')
                  "
                  tooltipPlacement="top"
                  :disabled="isEvalGridFieldsExtracting"
                  @click.native="onLlmExtractionGenerateClick(null)"
                />
              </div>
              <BaseRoundButton
                :size="44"
                :tooltip-text="
                  evaluationGridNotFound
                    ? $t('share-opportunity-dialog.share-disabled-no-grid')
                    : $t('documents-summary.share')
                "
                tooltipPlacement="top"
                class="opportunity-detail-page__card__actions-item"
                icon="send"
                is-white
                :disabled="evaluationGridNotFound"
                @click.native="onShare"
              />
            </div>
          </div>
          <div v-if="!tenantHasDocumentsAccess()" class="opportunity-detail-page__card-empty-text">
            {{ $t('subscriptions.message') }}
          </div>
          <div
            v-else-if="!evaluationGridLoading && evaluationGridNotFound"
            class="opportunity-detail-page__card-empty-text"
          >
            {{ $t('evaluation-grid.not-found') }}
          </div>
          <div v-else class="opportunity-detail-page__card-empty-text">
            <FieldsExtractionProgress />
            <DocumentsSummary />
          </div>
        </div>
        <div
          class="opportunity-detail-page__card opportunity-detail-page__keywords-card"
          v-if="shouldShowDocumentsModule"
        >
          <div class="opportunity-detail-page__card-title-container">
            <div class="opportunity-detail-page__card-title">
              <div>{{ $t('page.analysis.pdf-navigator.keywords') }}</div>
              <DocumentsWithoutKeywordsWarning
                v-if="tenantHasDocumentsAccess()"
                @document-click="goToDocumentAnalysis"
              />
              <BaseIcon v-if="!tenantHasDocumentsAccess()" name="lock" class="opportunity-detail-page__lock-icon" />
            </div>
          </div>
          <div v-if="!tenantHasDocumentsAccess()" class="opportunity-detail-page__card-empty-text">
            {{ $t('upgrade-messages.general') }}
          </div>
          <DocumentsKeywordsSummary v-else />
        </div>
      </div>
      <div class="opportunity-detail-page__right" v-if="hasLinkedTender">
        <OpportunitySourceTender :tenderId="opportunity.tenderId" />
      </div>
    </div>
    <PotentialBidderDialog ref="potentialBidderDialog" :opportunity-id="opportunityId" />
    <ConfirmationDialog
      ref="confirmationDialog"
      :buttonText="$t('actions.delete')"
      @execute="executeDeletePotentialBidder"
    >
      <template slot="content">
        <div>
          <i18n path="page.opportunity-details.potential-bidder-manipulations.confirmation-message">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
            </template>
            <template v-slot:organizationName v-if="manipulatedPotentialBidder">
              <span class="confirmation-dialog__action"> {{ manipulatedPotentialBidder.organizationName }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
    <EditJobLocationDialog ref="editJobLocationDialog" />
    <ConfirmationDialog
      ref="changeTemplateConfirmationDialog"
      :buttonText="$t('evaluation-grid.change-template-action')"
      @execute="executeChangeTemplate"
      @cancel-click="onCancelClicked"
    >
      <template slot="content">
        <div>
          <i18n path="evaluation-grid.change-template-confirm">
            <template v-slot:templateName>
              <span class="confirmation-dialog__action">{{
                pendingTemplateChange ? pendingTemplateChange.name : ''
              }}</span>
            </template>
          </i18n>
          <div v-if="!isEvaluationGridEmpty" class="dialog__warning-notice">
            {{
              tenantHasLLMExtractionsAccess()
                ? $t('evaluation-grid.change-template-warning-llm')
                : $t('evaluation-grid.change-template-warning-no-llm')
            }}
          </div>
        </div>
      </template>
    </ConfirmationDialog>
    <LlmExtractionGenerateDialog ref="llmExtractionGenerateDialog" @on-cancel="onCancelClicked" />
    <ShareOpportunityDialog ref="shareOpportunityDialog" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';
import { bus } from '@/main';
import DebounceMixin from '@/mixins/debounce-mixin';
import PlatformName from '@/mixins/platform-name-mixin';
import BaseAvatar from '@/components/base/BaseAvatar';
import BaseButton from '@/components/base/BaseButton';
import BaseMenu from '@/components/base/BaseMenu';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseIcon from '@/components/base/BaseIcon';
import BasePotentialBidder from '@/components/base/BasePotentialBidder';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';

import DatePicker from '@/components/form/DatePicker';
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import DocumentsSummary from '@/components/page-opportunity-details/DocumentsSummary';
import FieldsExtractionProgress from '@/components/page-opportunity-details/FieldsExtractionProgress.vue';
import DocumentsKeywordsSummary from '@/components/page-opportunity-details/DocumentsKeywordsSummary';
import OpportunityHeader from '@/components/page-opportunities/OpportunityHeader';
import OpportunityMetaCard from '@/components/page-opportunity-details/OpportunityMetaCard';
import OpportunityDocuments from '@/components/page-opportunity-details/OpportunityDocuments';
import PotentialBidderDialog from '@/components/page-opportunity-details/PotentialBidderDialog';
import OpportunitySourceTender from '@/components/page-opportunity-details/OpportunitySourceTender';
import DocumentsWithoutKeywordsWarning from '@/components/page-opportunity-details/DocumentsWithoutKeywordsWarning.vue';
import OpportunityMobilizations from '@/components/page-opportunity-details/OpportunityMobilizations.vue';
import { isCanadianProvinceReference } from '@/util/canadian-province-reference';
import {
  formatDeliveryLocation,
  getEffectiveDeliveryLocations,
  localizeJobLocationText,
} from '@/util/delivery-location-display';
import EditJobLocationDialog from '@/components/page-opportunity-details/EditJobLocationDialog.vue';
import DownloadDocumentsButton from '@/components/DownloadDocumentsButton.vue';
import DesktopDownloadDocumentsButton from '@/components/DesktopDownloadDocumentsButton.vue';
import DownloadAllDocumentsButton from '@/components/page-opportunity-details/DownloadAllDocumentsButton.vue';
import LlmExtractionGenerateDialog from '@/components/page-opportunity-details/LlmExtractionGenerateDialog.vue';
import ShareOpportunityDialog from '@/components/page-opportunity-details/ShareOpportunityDialog.vue';
import MarkdownEditor from '@/components/form/MarkdownEditor.vue';
import BaseInfoBanner from '@/components/base/BaseInfoBanner.vue';
import { isTenantUpsellRestricted } from '@/util/upsell-tenant-restriction-util';
import {
  tenantHasChromeExtensionAccess,
  tenantHasDocumentsAccess,
  tenantHasLLMExtractionsAccess,
} from '@/util/module-access-util';
import { paths } from '@/util/paths';
import { formatDate, formatShortDate, formatStandardDate, toMoment } from '@/util/date-util';

export default {
  name: 'PageOpportunityDetails',
  components: {
    MarkdownEditor,
    BaseInfoBanner,
    DocumentsWithoutKeywordsWarning,
    DownloadDocumentsButton,
    DesktopDownloadDocumentsButton,
    DownloadAllDocumentsButton,
    BaseRoundButton,
    OpportunityMobilizations,
    OpportunitySourceTender,
    BaseAvatar,
    BaseBottomSheet,
    BaseButton,
    BaseIcon,
    BasePotentialBidder,
    BaseCircularLoadingBar,
    BaseLoadingBar,
    BaseMenu,
    DatePicker,
    DocumentsSummary,
    FieldsExtractionProgress,
    DocumentsKeywordsSummary,
    OpportunityHeader,
    OpportunityMetaCard,
    OpportunityDocuments,
    LlmExtractionGenerateDialog,
    EditJobLocationDialog,
    ConfirmationDialog,
    PotentialBidderDialog,
    ShareOpportunityDialog,
  },
  mixins: [DebounceMixin, PlatformName],
  props: {
    opportunityId: String,
  },
  data() {
    return {
      manipulatedPotentialBidder: null,
      dates: [],
      dateSheetOpen: false,
      notesSaving: false,
      selectedTemplate: [],
      pendingTemplateChange: null,
      pollSubscriber: null,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['users', 'tenant']),
    ...mapGetters('tenantLocations', ['tenantLocations']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('opportunity', { opportunityPending: 'pending' }),
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('potentialBidders', ['potentialBidders']),
    ...mapGetters('potentialBidders', { potentielBiddersPending: 'pending' }),
    ...mapGetters('documentKeywords', { documentKeywords: 'keywords' }),
    ...mapGetters('fieldsExtraction', { isEvalGridFieldsExtracting: 'isExtracting' }),
    ...mapGetters('evaluationGridTemplates', ['evaluationGridTemplates']),
    ...mapGetters('evaluationGrid', {
      evaluationGridLoading: 'pending',
      evaluationFields: 'evaluationFields',
      evaluationGridNotFound: 'notFound',
    }),
    ...mapGetters('annotations', { annotations: 'annotations' }),
    tenantIsRestricted() {
      return isTenantUpsellRestricted(this.currentUser && this.currentUser.tenantId);
    },
    shouldShowDocumentsModule() {
      return this.tenantHasDocumentsAccess() || !this.tenantIsRestricted;
    },
    editorOptions() {
      return {
        placeholder: this.$t('page.opportunity-details.notes.placeholder'),
      };
    },
    tender() {
      return this.opportunity.tender;
    },
    hasLinkedTender() {
      return this.opportunity && this.opportunity.tenderId;
    },
    datesRangeText() {
      const dates = [...this.dates]
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => formatDate(toMoment(date)))
        .join(` ${this.$t('basic-words.to')} `);
      return dates || this.$t('date-picker.placeholder');
    },
    jobLocationText() {
      const location = this.opportunity.jobLocation;
      if (!location) return '';
      if (this.jobLocationNoticeKey === 'page.opportunity-details.mobilization.approximate-area') {
        const delivery = getEffectiveDeliveryLocations(this.tender)[0];
        return formatDeliveryLocation(
          delivery,
          (key) => this.$t(key),
          (key) => this.$te(key),
        );
      }
      if (
        this.jobLocationNoticeKey === 'page.opportunity-details.mobilization.approximate-province' &&
        location.region
      ) {
        return formatDeliveryLocation(
          { region: location.region, countryName: location.countryName },
          (key) => this.$t(key),
          (key) => this.$te(key),
        );
      }
      return localizeJobLocationText(
        location,
        (key) => this.$t(key),
        (key) => this.$te(key),
      );
    },
    jobLocationNoticeKey() {
      const location = this.opportunity && this.opportunity.jobLocation;
      if (!location) return null;
      if (location.longitude == null || location.latitude == null) {
        return 'page.opportunity-details.mobilization.unroutable-location';
      }
      const deliveries = getEffectiveDeliveryLocations(this.tender);
      const delivery = deliveries.length === 1 ? deliveries[0] : null;
      const rangeText =
        delivery && /^\s*\d{1,6}\s*[-–]\s*\d{1,6}\s+\S/.test(delivery.streetAddress || '')
          ? [
              delivery.streetAddress,
              delivery.locality,
              delivery.area,
              delivery.region,
              delivery.postalCode,
              delivery.countryName,
            ]
              .filter(Boolean)
              .join(', ')
          : null;
      if (rangeText && location.addressText === rangeText) {
        return 'page.opportunity-details.mobilization.approximate-address-range';
      }
      const areaText =
        delivery && delivery.area && !delivery.streetAddress && !delivery.locality && !delivery.postalCode
          ? [delivery.area, delivery.region, delivery.countryName].filter(Boolean).join(', ')
          : null;
      if (areaText && [areaText, delivery.raw].includes(location.addressText)) {
        return 'page.opportunity-details.mobilization.approximate-area';
      }
      const provinceText = isCanadianProvinceReference(delivery)
        ? [delivery.region, delivery.countryName].filter(Boolean).join(', ')
        : null;
      if (
        provinceText &&
        [provinceText, delivery.raw].includes(location.addressText) &&
        !location.city &&
        !location.postalCode
      ) {
        return 'page.opportunity-details.mobilization.approximate-province';
      }
      const cityText =
        delivery && delivery.locality && !delivery.streetAddress && !delivery.postalCode
          ? [delivery.locality, delivery.area, delivery.region, delivery.countryName].filter(Boolean).join(', ')
          : null;
      if (cityText && location.addressText === cityText) {
        return 'page.opportunity-details.mobilization.approximate-city';
      }
      const postcodeText =
        delivery && delivery.postalCode && !delivery.streetAddress && !delivery.area
          ? [delivery.locality, delivery.region, delivery.postalCode, delivery.countryName].filter(Boolean).join(', ')
          : null;
      if (postcodeText && location.addressText === postcodeText) {
        return 'page.opportunity-details.mobilization.approximate-postcode';
      }
      return null;
    },
    missingTenderDocuments() {
      if (!this.opportunity) {
        return [];
      }
      // If there are no opportunity documents yet, return none (empty array)
      if (!Array.isArray(this.opportunity.documents) || this.opportunity.documents.length === 0) {
        return [];
      }
      if (!this.opportunity.tender || !Array.isArray(this.opportunity.tender.documents)) {
        return [];
      }
      // Helper to strip file extension
      const stripExtension = (filename) => {
        if (!filename) return '';
        return filename.replace(/\.[^/.]+$/, '');
      };
      // Create a set of normalized names (without extension) from opportunity.documents[].name
      const opportunityDocumentNames = new Set(
        this.opportunity.documents
          .map((doc) => this.normalizeDocumentName(stripExtension(doc && doc.name)))
          .filter((name) => name),
      );
      // Return original tender document titles that are missing from opportunity documents
      return this.opportunity.tender.documents
        .filter((doc) => {
          const normalizedTitle = this.normalizeDocumentName(doc && doc.title);
          return normalizedTitle && !opportunityDocumentNames.has(normalizedTitle);
        })
        .map((doc) => doc && doc.title)
        .filter((title) => !!title);
    },
    evaluationGridTemplateName() {
      if (
        this.opportunity &&
        this.opportunity.evaluationGrid &&
        this.evaluationGridTemplates &&
        this.evaluationGridTemplates.length > 0
      ) {
        const templateUsed = this.evaluationGridTemplates.find(
          (t) => t.id === this.opportunity.evaluationGrid.evaluationGridTemplateId,
        );
        if (templateUsed) {
          return templateUsed.name;
        }
      }
      return '';
    },
    isEvaluationGridEmpty() {
      return !this.evaluationFields || !this.evaluationFields.some((field) => field.values && field.values.length > 0);
    },
    // TODO: check if needed
    // True only when the grid has actually been loaded (fields present).
    // Distinguishes "no data yet / being reloaded" from "loaded but values empty".
    // Used to prevent a flash of the "Remplir la grille" button during the
    // brief window between the status mutation (pending=false) and the grid
    // payload mutation in GET_EVALUATION_GRID_SUCCESS, and while transitioning
    // between pages with different opportunity state.
    isEvaluationGridLoaded() {
      return Array.isArray(this.evaluationFields) && this.evaluationFields.length > 0;
    },
    manualPotentialBidders() {
      return this.potentialBidders
        ? this.potentialBidders.map((b) => ({
            id: b.id,
            opportunity_id: b.opportunityId,
            organization_name: b.organizationName,
            address: b.organizationAddress,
            website: b.organizationWebsite,
            contact_points: [
              { name: b.contactName, telephone: b.contactPhone, email: b.contactEmail, fax: b.contactFax },
            ],
            is_manual_entry: true,
          }))
        : [];
    },
    hasDocuments() {
      return this.opportunity && this.opportunity.documents && this.opportunity.documents.length > 0;
    },
    hasAnnotations() {
      return this.annotations && this.annotations.length > 0;
    },
  },
  watch: {
    'opportunity.documents'() {
      if (this.pollSubscriber) {
        this.stopPollForQueuedDocuments({ subscriber: this.pollSubscriber });
      }
      this.pollSubscriber = this.pollForQueuedDocuments({ opportunityId: this.opportunityId });
    },
  },
  methods: {
    tenantHasChromeExtensionAccess,
    ...mapActions('opportunity', ['getOpportunity', 'editOpportunity']),
    ...mapActions('potentialBidders', ['getPotentialBidders', 'deletePotentialBidder']),
    ...mapActions('documentKeywords', ['getKeywords']),
    ...mapActions('opportunityDocumentsPdfEngine', ['getOpportunityMatchInformation']),
    ...mapActions('opportunityDocumentsQueue', ['pollForQueuedDocuments', 'stopPollForQueuedDocuments']),
    ...mapActions('extractionJob', ['watchExtractionJob']),
    ...mapActions('evaluationGridTemplates', ['getEvaluationGridTemplates']),
    ...mapActions('evaluationGrid', ['getEvaluationGrid', 'resetEvaluationGrid']),
    ...mapActions('annotations', ['getOpportunityDocumentsAnnotations']),
    tenantHasDocumentsAccess,
    tenantHasLLMExtractionsAccess,
    goToDocumentAnalysis(document) {
      this.openDocumentAnalysis({ documentId: document.id });
    },
    onDocumentsUploaded() {
      this.getOpportunity({ id: this.opportunityId, silent: true });
      if (this.tenantHasLLMExtractionsAccess()) {
        this.watchExtractionJob({ opportunityId: this.opportunityId });
      }
    },
    onDocumentDeleted() {
      // Refresh annotations and evaluation grid (cascade-deleted in DB)
      this.getOpportunityDocumentsAnnotations({ opportunityId: this.opportunityId });
      this.getEvaluationGrid(this.opportunityId);
      // The matches are reported per document and still hold those of the deleted one
      this.getOpportunityMatchInformation({ opportunityId: this.opportunityId });
    },
    normalizeDocumentName(document) {
      if (!document) return '';
      const name = typeof document === 'string' ? document : document.name;
      if (!name) return '';
      return name.toString().trim().toLowerCase();
    },
    formatShortDate,
    formatDate,
    formatStandardDate,
    toMoment,
    onCopyLocation() {
      navigator.clipboard
        .writeText(this.opportunity.mobilizations[0].toLocation.addressText)
        .then(() => {
          this.$toast.success(this.$t('page.analysis.text-selection.copy.success'));
        })
        .catch(() => {
          this.$toast.error(this.$t('page.analysis.text-selection.copy.error'));
        });
    },
    resetSelectedTemplate() {
      const templateId = this.opportunity.evaluationGrid && this.opportunity.evaluationGrid.evaluationGridTemplateId;
      const template = templateId && this.evaluationGridTemplates.find((t) => t.id === templateId);
      this.selectedTemplate = template ? [template] : [];
    },
    async onTemplateSelect(selectedTemplate) {
      if (!selectedTemplate) return;
      this.selectedTemplate = [selectedTemplate];

      // Case user has LLM module + has documents → llm extraction regeneration dialog
      if (tenantHasLLMExtractionsAccess() && this.hasDocuments) {
        this.$refs.llmExtractionGenerateDialog.openDialog(this.opportunity.id, selectedTemplate.id);
      } else {
        // Cases user has no LLM module or no documents → template change confirmation dialog
        this.pendingTemplateChange = selectedTemplate;
        this.$refs.changeTemplateConfirmationDialog.openDialog();
      }
    },
    async executeChangeTemplate() {
      if (!this.pendingTemplateChange) return;
      const selectedTemplate = this.pendingTemplateChange;
      this.pendingTemplateChange = null;

      try {
        await this.editOpportunity({
          opportunityId: this.opportunity.id,
          opportunity: {
            name: this.opportunity.name,
            evaluationGridTemplateId: selectedTemplate.id,
            statusId: this.opportunity.status ? this.opportunity.status.id : null,
            startDate: this.opportunity.startDate || null,
            endDate: this.opportunity.endDate || null,
            source: this.opportunity.source,
            emitter: this.opportunity.emitter,
            notes: this.opportunity.notes || '',
            version: this.opportunity.version || 0,
          },
        });
        await this.refreshOpportunityAndGrid(this.opportunity.id);
      } catch {
        this.resetSelectedTemplate();
        this.$toast.error(this.$t('toast.error'));
      }
    },
    async refreshOpportunityAndGrid(opportunityId) {
      await this.getOpportunity({ id: opportunityId, silent: true });
      this.getEvaluationGrid(opportunityId);
    },
    onLlmExtractionGenerateClick(selectedTemplate) {
      if (selectedTemplate) {
        this.selectedTemplate = [selectedTemplate];
      }
      const templateId = selectedTemplate && selectedTemplate.id ? selectedTemplate.id : null;
      this.$refs.llmExtractionGenerateDialog.openDialog(this.opportunity.id, templateId);
    },
    isCurrentUser(userId) {
      return userId === this.currentUser.id;
    },
    getFullName(authorId) {
      if (this.users) {
        const user = this.users.find((user) => user.id === authorId);
        if (!user) return;
        return user.fullName;
      }
    },
    emailFinder(authorId) {
      if (this.users) {
        const user = this.users.find((user) => user.id === authorId);
        if (!user) return;
        return user.email;
      }
    },
    handleError(error) {
      if (!error.response) this.$router.replace(paths.notFound);

      // If a not found or forbidden error occurs, redirect to not-found page
      if (error.response.status === 404 || error.response.status === 403) {
        this.$router.replace(paths.notFound);
      } else {
        // Otherwise show an error toast
        console.error(error);
        this.$toast.error(error);
        throw error;
      }
    },
    onAddPotentialBidder() {
      this.$refs.potentialBidderDialog.openAddPotentialBidderDialog();
    },
    onEditPotentialBidder(potentialBidder) {
      this.$refs.potentialBidderDialog.openEditPotentialBidderDialog(potentialBidder);
    },
    onDeletePotentialBidder(potentialBidder) {
      this.manipulatedPotentialBidder = potentialBidder;
      this.$refs.confirmationDialog.openDialog();
    },
    executeDeletePotentialBidder() {
      this.deletePotentialBidder({
        opportunityId: this.manipulatedPotentialBidder.opportunity_id,
        potentialBidderId: this.manipulatedPotentialBidder.id,
      })
        .then(() => {
          this.$toast.success(this.$t('page.opportunity-details.potential-bidder-manipulations.deleted'));
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    openDateSheet() {
      if (this.isPhone) this.dateSheetOpen = true;
    },
    onEditDate() {
      this.dates = [formatDate(toMoment(this.opportunity.startDate), null).slice(0, 10)];
      if (this.opportunity.endDate) {
        this.dates.push(formatDate(toMoment(this.opportunity.endDate), null).slice(0, 10));
      }
    },
    saveDates(dates) {
      this.dates = dates;
      const sortedDates = dates.sort((a, b) => new Date(a) - new Date(b));
      // The menu closes keeping them (its return value); the sheet just closes.
      if (this.isPhone) this.dateSheetOpen = false;
      else this.$refs.datePickerMenu.save(dates);

      const formattedStartDate = formatDate(toMoment(sortedDates[0]), null).replace('T', ' ');
      const formattedEndDate = formatDate(toMoment(sortedDates[1]), null).replace('T', ' ');
      const body = this.buildOpportunityBody({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      this.editOpportunity({
        opportunityId: this.opportunity.id,
        opportunity: body,
      });
    },
    buildOpportunityBody(overrides) {
      return {
        name: this.opportunity.name,
        evaluationGridTemplateId: null,
        statusId: this.opportunity.status ? this.opportunity.status.id : null,
        startDate: formatDate(toMoment(this.dates[0]), null).replace('T', ' '),
        endDate: formatDate(toMoment(this.dates[1]), null).replace('T', ' '),
        source: this.opportunity.source,
        emitter: this.opportunity.emitter,
        notes: this.opportunity.notes ? this.opportunity.notes : '',
        version: this.opportunity.version || 0,

        ...(overrides || {}),
      };
    },
    openJobLocationDialog() {
      this.$refs.editJobLocationDialog.openDialog(this.opportunity);
    },
    openDocumentAnalysis({ documentId, page, fieldValueId }) {
      if (!documentId) return;
      this.$router.push({
        name: 'opportunity-analysis',
        params: { opportunityId: this.opportunity.id, documentId: documentId },
        query: { page: page || undefined, fieldValueId: fieldValueId || undefined },
      });
    },
    onCancelClicked() {
      this.resetSelectedTemplate();
      this.pendingTemplateChange = null;
    },
    async onChromeExtensionClosed(payload) {
      const oppId = payload.detail.message.task.opportunityId;
      if (!oppId) {
        console.warn('No opportunity ID found in the payload');
        return;
      }
      if (!this.opportunityId || this.opportunityId !== oppId) {
        return;
      }
      // refresh the opportunity
      await this.getOpportunity({ id: oppId });
    },
    async saveOpportunityNotes(markdown) {
      try {
        this.notesSaving = true;

        const body = this.buildOpportunityBody({
          notes: markdown,
        });

        await this.editOpportunity({
          opportunityId: this.opportunity.id,
          opportunity: body,
        });

        this.$set(this.opportunity, 'notes', markdown);
      } finally {
        this.notesSaving = false;
      }
    },
    async refreshOpportunityBeforeEditing() {
      if (!this.opportunity || !this.opportunity.id) return;
      await this.getOpportunity({ id: this.opportunity.id, silent: true });
    },
    onShare() {
      this.$refs.shareOpportunityDialog.openDialog(this.opportunity.id);
    },
    /**
     * Refetches the keyword matches after the document keywords changed, so that the counts of the
     * "Mots-clés" summary reflect the new keywords instead of the ones the page was loaded with.
     * Debounced for the same reason as in PageOpportunityAnalysis: keyword edits come in bursts and each
     * recomputation costs one search per keyword of the tenant.
     */
    onDocumentKeywordsChanged() {
      this.debounce(() => this.getOpportunityMatchInformation({ opportunityId: this.opportunityId }), 1500);
    },
  },
  mounted() {
    bus.$on('open-source-document', ({ documentId, page, fieldValueId }) => {
      this.openDocumentAnalysis({ documentId, page, fieldValueId });
    });
    bus.$on('document-keywords-changed', this.onDocumentKeywordsChanged);
    window.addEventListener('aloha:tabClosed', this.onChromeExtensionClosed);
  },
  async created() {
    try {
      const id = this.opportunityId;
      const isSwitchingOpportunity = this.opportunity == null || this.opportunity.id !== id;

      if (isSwitchingOpportunity) {
        const opportunity = await this.getOpportunity({ id });
        this.resetEvaluationGrid();
        if (!opportunity) return;
      }
      this.getPotentialBidders(id);

      if (this.tenantHasLLMExtractionsAccess()) {
        this.watchExtractionJob({ opportunityId: this.opportunityId });
      }
      if (this.tenantHasDocumentsAccess()) {
        // Fetch in parallel so templates refresh does not run after the grid is
        // already painted (which previously caused a DocumentsSummary reload flash).
        await Promise.all([this.getEvaluationGrid(id), this.getEvaluationGridTemplates()]);
        this.pollSubscriber = this.pollForQueuedDocuments({ opportunityId: this.opportunityId });

        this.resetSelectedTemplate();

        this.getKeywords().then(() => {
          if (this.documentKeywords.length > 0) {
            this.getOpportunityMatchInformation({ opportunityId: id });
          }
        });

        this.getOpportunityDocumentsAnnotations({
          opportunityId: this.opportunityId,
        });
      }

      this.dates = [this.opportunity.startDate];
      if (this.opportunity.endDate) {
        this.dates.push(this.opportunity.endDate);
      }
    } catch (error) {
      this.handleError(error);
    }
  },
  beforeDestroy() {
    this.stopPollForQueuedDocuments({ subscriber: this.pollSubscriber });
    bus.$off('open-source-document');
    // Passes the handler so that the listener of another view on the same event is left in place
    bus.$off('document-keywords-changed', this.onDocumentKeywordsChanged);
    // Drops a refetch still pending from a last-moment keyword change
    this.debounce(null, 0, true);
    window.removeEventListener('aloha:tabClosed', this.onChromeExtensionClosed);
  },
};
</script>

<style lang="scss">
.opportunity-detail-page {
  &__body {
    padding: 32px;
    display: flex;
    flex-direction: row;
    gap: 16px;

    // The three columns need a desktop: below it, the tender goes under the left column, at
    // the width it has there.
    @include screen-below('desktop') {
      display: grid;
      grid-template-areas:
        'left center'
        'right center';
      grid-template-columns: 340px minmax(0, 1fr);
      grid-template-rows: auto 1fr;
      align-items: start;
    }

    // Tablets and phones: one column. The columns step aside (`display: contents`) so their
    // cards make one flow: where the opportunity stands, the rest as on a desktop, and its
    // tender after the evaluation grid.
    @include screen-below('laptop') {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      padding: 24px;
    }

    @include screen-below('tablet') {
      padding: $phone-page-gutter;
    }
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 22%;
    min-width: 340px;

    @include screen-below('desktop') {
      grid-area: left;
      width: auto;
      min-width: 0;
    }

    @include screen-below('laptop') {
      display: contents;
    }
  }

  &__right {
    width: 25%;
    min-width: 340px;

    @include screen-below('desktop') {
      grid-area: right;
      width: auto;
      min-width: 0;
    }

    @include screen-below('laptop') {
      display: contents;
    }
  }

  .opportunity-meta-card {
    @include screen-below('laptop') {
      order: -1;
    }
  }

  &__keywords-card {
    @include screen-below('laptop') {
      order: 1;
    }
  }

  // Phones: 16px inside the cards, as from the screen's edges (see __base-card and __card too).
  @include screen-below('tablet') {
    .opportunity-meta-card,
    .opportunity-source-tender__base-card {
      padding: 16px;
    }
  }

  &__dates {
    display: flex;
    flex-direction: row;
    gap: 4px;
    color: $color-normal-pale;
  }

  &__base-card {
    padding: 32px;
    border-radius: $border-radius-root;
    background: $color-white;

    @include screen-below('tablet') {
      padding: 16px;
    }
  }

  &__card-content {
    display: flex;
    gap: 16px;
    flex-direction: column;
  }

  &__creator {
    display: flex;
    flex-direction: row;
  }

  &__created-at {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  &__creator-identity {
    display: flex;
    flex-direction: column;
    margin-left: 8px;
  }

  &__creator-email {
    color: $color-normal-pale;
    font-size: 12px;
  }

  &__create-date {
    color: $color-neutral;
    font-size: 12px;
  }

  &__card {
    background: $color-white;
    padding: 32px;
    border-radius: $border-radius-root;

    @include screen-below('tablet') {
      padding: 16px;
    }

    .base-button {
      .ai-icon {
        color: $color-white;
      }
    }
  }

  &__card-title-container {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    > div {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .base-select {
      max-width: 250px;
    }

    .base-button {
      font-weight: bold;
    }

    // Phones: the evaluation grid's title on its own line, its template and buttons under it.
    @include screen-below('tablet') {
      flex-wrap: wrap;
      row-gap: 8px;

      > .opportunity-detail-page__grid-heading {
        display: contents;

        .opportunity-detail-page__card-title {
          flex-basis: 100%;
        }
      }
    }

    // Round icon actions side by side, beside a title on one line; under it when they don't fit (the desktop robot's
    // progress text)
    &--icon-actions {
      flex-wrap: wrap;
      row-gap: 4px;

      .opportunity-detail-page__card-title {
        white-space: nowrap;
      }

      > .opportunity-detail-page__card-title-right-section {
        gap: 2px;

        // BaseRoundButton has no outline, and no focus ring when transparent: a keyboard user needs one
        .base-round-button:focus-visible {
          box-shadow: $shadow-focus;
        }
      }
    }
  }

  &__card-title {
    color: $color-normal;
    font-size: 20px;
    font-weight: 800;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    &.no-padding {
      padding-bottom: 0;
    }

    &-right-section {
      justify-content: flex-end;
      align-items: center;
      margin-left: auto;
      display: flex;
      gap: 2px;

      .base-chip {
        height: fit-content;
      }

      .tag.ai {
        margin-right: 8px;
      }
    }
  }

  &__card-subtitle {
    color: $color-normal;
    font-size: 14px;
    font-weight: 800;
    padding-bottom: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    &--text {
      font-weight: bold;
    }
  }

  &__card-empty-text {
    color: $color-neutral;
  }

  &__potential-bidders-tags {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__switch-evaluation-grid {
    margin-right: 8px;
  }

  &__chip {
    background-color: $color-neutral-pale;
    padding: 4px 8px;
    border-radius: $border-radius-small;
    color: $color-normal-pale;
    font-size: 14px;
    display: block;
    width: fit-content;
  }

  &__add-icon {
    height: 14px;
    width: 14px;
    margin-right: 4px;
  }

  &__lock-icon {
    width: 18px;
    height: 18px;
    color: $color-neutral;
  }

  &__center {
    flex: 1;
    display: flex;
    gap: 16px;
    flex-direction: column;

    @include screen-below('desktop') {
      grid-area: center;
      min-width: 0;
    }

    @include screen-below('laptop') {
      display: contents;
    }
  }

  .markdown-editor-field__title {
    font-weight: 800;
    font-size: 20px;
  }

  #fetch-documents-button {
    display: none;

    &.base-button.disabled {
      .base-button__element {
        box-shadow: none;
        cursor: not-allowed;
        opacity: 0.6;
      }
    }
  }

  .base-icon {
    &.alert {
      color: $color-yellow;
    }
  }

  .potential-bidder-tag.__size-compact {
    width: 100%;
  }

  &__mobilization {
    margin-top: 16px;
  }

  &__location-notice {
    margin-top: 12px;
  }

  &__missing-docs-banner {
    margin-bottom: 12px;
  }

  .v-popover .trigger {
    width: 100% !important;
  }
}
</style>
